import { action, computed, observable, store } from '~/common/mobx.decorator'
import { apolloClient, CALENDAR_VIEW_QUERY } from '~/common/apollo'
import { notifyStore } from '~/stores'
import { PATHS } from '~/common/constants'
import { routerStore } from '~/stores'
import { JOB_BASED_FRAGMENT } from '~/common/apollo/apollo.fragment'
import { gql } from '@apollo/client'
import { careStore } from '~/companies/care/care.store'
const moment = require('moment-timezone')

const GET_LIST_JOBS_QUERY = gql`
	query getListJobsQuery($filter: JobFilter, $sortBy: JobManagementSort, $after: String, $before: String, $first: Int, $last: Int) {
		searchJobs(filter: $filter, sortBy: $sortBy, after: $after, before: $before, first: $first, last: $last) {
			totalCount
			nodes {
				...jobBasedFragment
			}
			pageInfo {
				endCursor
				hasNextPage
				pageSize
				startCursor
			}
		}
	}
	${JOB_BASED_FRAGMENT}
`

const SORT_OPTIONS = [
	{ label: 'Start date/time', value: 'START_DATETIME', sortBy: { field: 'START_DATETIME', order: 'desc' } },
	{ label: 'Matching score', value: 'SCORE', sortBy: { field: 'SCORE', order: 'desc' } },
]

@store()
class AvailablePerdiemShiftStore {
	@observable totalCount
	@observable defaultSavedSearch
	@observable calendarJobs = []
	@observable view = 'month'
	@observable instruction =
		'<div style="color: #c4c4c4;font-size: 14px;font-style: normal;font-weight: 500;line-height: 22px;letter-spacing: 0.28px;"> <div style="color: #f8f8f8;font-size: 16px;font-style: normal;font-weight: 800;line-height: 24px;"> Per diem scheduler </div> <p> Your calendar displays both your assigned per diem shifts and any others that may be available. </p> <div style="color: #f8f8f8;font-size: 16px;font-style: normal;font-weight: 800;line-height: 24px;"> Assigned shifts </div> <p>Assigned per diem shifts are those you accepted and have been approved.</p> <div style="color: #f8f8f8;font-size: 16px;font-style: normal;font-weight: 800;line-height: 24px;"> Available shifts </div> <p> Available per diem shifts are selected and suggested for you based on your specific profession, specialty, work experience, and preferences. </p><p>You can click on any day to view more details of your shifts.</p></div>'

	@observable selectedDate = null
	@observable activeTab = 'available'
	@observable searchJobs = []
	@observable isFetching = false
	@observable sortValue = 'SCORE'

	@computed
	get events() {
		return (
			this.calendarJobs?.flatMap((viewItem) => {
				const date = viewItem?.date
				return (
					viewItem?.data?.workerAssignments?.map((assignment) => ({
						id: assignment?.id,
						title: assignment?.job?.jobGeneral?.title,
						allDay: true,
						start: date,
						end: date,
						view: this.view,
						status: assignment?.status,
						...assignment,
					})) || []
				)
			}) || []
		)
	}

	@computed
	get sortOptions() {
		return SORT_OPTIONS
	}

	@computed
	get selectedAssignments() {
		const selectedDate = moment(this.selectedDate).format('YYYY-MM-DD')
		return this.calendarJobs.find((viewItem) => viewItem?.date === selectedDate)?.data?.workerAssignments || []
	}

	@computed
	get shiftsPerday() {
		return this.calendarJobs?.reduce((acc, viewItem) => ({ ...acc, [viewItem.date]: viewItem?.data?.nonLongTermPerDiemJobsCount || 0 }), {})
	}

	@computed
	get eventsPerDay() {
		return this.calendarJobs?.reduce((acc, viewItem) => ({ ...acc, [viewItem.date]: viewItem?.data?.workerAssignments?.length || 0 }), {})
	}

	@action
	setView = (view) => {
		this.view = view
	}

	@action
	setSortValue = (value) => {
		this.sortValue = value
		this.searchJobs = {}
		this.fetchJobs()
	}

	@action
	fetchAssignmentsByRange = async (from, to) => {
		try {
			const { data } = await apolloClient.query({
				query: CALENDAR_VIEW_QUERY,
				variables: { fromDate: moment(from).format('YYYY-MM-DD'), toDate: moment(to).format('YYYY-MM-DD') },
			})
			this.calendarJobs = data.calendarViewPerDiem
		} catch (error) {
			notifyStore.error(error.message)
		}
	}

	@action
	fetchJobs = async (isMore = false) => {
		if (this.isFetching) {
			return
		}

		if (isMore && !this.searchJobs?.pageInfo?.hasNextPage) {
			return
		}

		const timezone = careStore.tenant
		const momentDate = moment.tz(moment(this.selectedDate).format('YYYY-MM-DD'), timezone)
		const { verifiedPreferredLocations } = careStore
		try {
			this.isFetching = true
			const sortBy = SORT_OPTIONS.find((item) => item.value === this.sortValue)?.sortBy
			const response = await apolloClient.query({
				query: GET_LIST_JOBS_QUERY,
				variables: {
					filter: {
						isPerDiem: true,
						isPerDiemLongTerm: false,
						startDatetimeBetween: [momentDate.startOf('day').toISOString(), momentDate.endOf('day').toISOString()],
						addressStates: verifiedPreferredLocations,
					},
					first: 20,
					after: isMore ? this.searchJobs?.pageInfo?.endCursor : null,
					sortBy,
				},
			})

			if (isMore) {
				const nodes = response.data?.searchJobs?.nodes || []

				this.searchJobs = {
					...response.data?.searchJobs,
					nodes: [...this.searchJobs?.nodes, ...nodes],
				}
			} else {
				this.searchJobs = response.data?.searchJobs
			}
		} finally {
			this.isFetching = false
		}
	}

	@action
	handleSelectEvent = async (event) => {
		if (event?.job?.id) {
			routerStore.goPage(`${PATHS.care.jobs}/${event?.job?.id}`)
		}

		event.stopPropagation()
	}

	@action
	handleSelectDate = (date) => {
		this.selectedDate = date
		this.searchJobs = {}
		this.sortValue = 'START_DATETIME'
	}

	@action
	clearData = () => {
		this.selectedDate = null
		this.activeTab = 'available'
	}

	@action
	setActiveTab = (tab) => {
		this.activeTab = tab
	}
}

export const availablePerdiemShiftStore = new AvailablePerdiemShiftStore()
